/**
 * Centralized query key factory.
 *
 * Rules:
 *  1. Every key is a const-tuple — safe to use in `invalidateQueries` matchers.
 *  2. `orgId` is always included so account-switching automatically triggers
 *     new fetches (different key = different cache entry).
 *  3. Nested structure mirrors the resource hierarchy, enabling partial
 *     invalidation: `keys.alerts.all(orgId)` clears every alerts cache entry
 *     for that org in one call.
 *
 * Usage:
 *   queryKey: keys.connectors.list(orgId)
 *   invalidateQueries({ queryKey: keys.connectors.all(orgId) })
 */
/**
 * Everything about the caller that can change what the API returns for one URL.
 *
 * `UserContext` is assignable to this, so call sites pass it straight through.
 */
export interface AuthScope {
  orgId?: string;
  role?: string;
  sub?: string;
}

export const authScopeKey = (auth?: AuthScope | null) =>
  [auth?.orgId ?? null, auth?.role ?? null, auth?.sub ?? null] as const;

export const keys = {
  connectors: {
    all:        (orgId?: string) => ["connectors", orgId] as const,
    list:       (orgId?: string) => ["connectors", orgId, "list"] as const,
    detail:     (orgId?: string, id?: string) => ["connectors", orgId, id, "detail"] as const,
    health:     (orgId?: string, id?: string) => ["connectors", orgId, id, "health"] as const,
    executions: (orgId?: string, id?: string) => ["connectors", orgId, id, "executions"] as const,
    activity:   (orgId?: string, id?: string, actionId?: string) =>
                  ["connectors", orgId, id, "activity", actionId ?? null] as const,
    versionUsage: (orgId?: string, slug?: string) =>
                    ["connectors", orgId, "version-usage", slug ?? null] as const,
    versionPins: (orgId?: string) => ["connectors", orgId, "version-pins"] as const,
    entities:   (orgId?: string, id?: string) => ["connectors", orgId, id, "entities"] as const,
  },

  /* The one family keyed on the whole auth context, not just orgId, because it
     is the one whose RESPONSE BODY varies by role: GET /api/v1/actions runs its
     rows through `gatedAttribution`, which resolves createdBy/updatedBy actors
     for `platform_admin` and returns an empty map for everyone else
     (services/api/src/routes/connectors.ts:1350). Same org, same connector, same
     URL, two different payloads.

     orgId alone would not separate them. Impersonation is org-level, so it does
     move orgId — but a role can change without the org changing (a role edit
     that lands on the next token refresh; a platform_admin who, per the
     account-switcher cutover, carries no org_id claim at all). `sub` is in here
     for the same reason and costs nothing: within one session it is constant,
     so it never splits an entry that should be shared.

     It matters more here than for the org-only keys because
     `fetchConnectorActions` reads this entry IMPERATIVELY, outside any hook, so
     a stale entry is served rather than merely re-rendered. */
  actions: {
    all:  (auth?: AuthScope | null) => ["actions", ...authScopeKey(auth)] as const,
    list: (auth?: AuthScope | null, connectorId?: string) =>
            ["actions", ...authScopeKey(auth), connectorId ?? null] as const,
  },

  events: {
    all:  (orgId?: string) => ["events", orgId] as const,
    // The filters are part of the key: without them a type change or a new page
    // re-serves the rows already in the cache, which is what made Next look
    // like it did nothing.
    list: (orgId?: string, filters?: Record<string, unknown>) =>
      ["events", orgId, "list", filters ?? null] as const,
    counts: (orgId?: string, filters?: Record<string, unknown>) =>
      ["events", orgId, "counts", filters ?? null] as const,
  },

  alerts: {
    all:  (orgId?: string) => ["alerts", orgId] as const,
    list: (orgId?: string) => ["alerts", orgId, "list"] as const,
  },

  syncJobs: {
    all:  (orgId?: string) => ["sync-jobs", orgId] as const,
    list: (orgId?: string) => ["sync-jobs", orgId, "list"] as const,
  },

  users: {
    all:     (orgId?: string) => ["users", orgId] as const,
    list:    (orgId?: string) => ["users", orgId, "list"] as const,
    pending: (orgId?: string) => ["users", orgId, "pending"] as const,
  },

  teams: {
    all:  (orgId?: string) => ["teams", orgId] as const,
    list: (orgId?: string) => ["teams", orgId, "list"] as const,
  },

  executions: {
    all:  (orgId?: string) => ["executions", orgId] as const,
    list: (orgId?: string, params?: Record<string, string | undefined>) =>
            ["executions", orgId, "list", params] as const,
  },

  syncReports: {
    all:  (orgId?: string) => ["sync-reports", orgId] as const,
    list: (orgId?: string, params?: Record<string, string | undefined>) =>
            ["sync-reports", orgId, "list", params] as const,
  },

  workflows: {
    all:          (orgId?: string) => ["workflows", orgId] as const,
    list:         (orgId?: string, tenantOverride?: string) => ["workflows", orgId, "list", tenantOverride ?? null] as const,
    detail:       (orgId?: string, id?: string) => ["workflows", orgId, id] as const,
    executions:   (orgId?: string, id?: string) => ["workflows", orgId, id, "executions"] as const,
    execution:    (orgId?: string, wfId?: string, execId?: string) => ["workflows", orgId, wfId, "executions", execId] as const,
    executionLogs: (orgId?: string, wfId?: string, execId?: string) => ["workflows", orgId, wfId, "executions", execId, "logs"] as const,
    logSearch:    (orgId?: string, params?: Record<string, string>) => ["workflows", orgId, "executions", "logs", params ?? {}] as const,
    versions:     (orgId?: string, id?: string) => ["workflows", orgId, id, "versions"] as const,
    version:      (orgId?: string, id?: string, versionId?: string) => ["workflows", orgId, id, "versions", versionId] as const,
    docs:         (orgId?: string, id?: string, audience?: string) => ["workflows", orgId, id, "docs", audience ?? "end_user"] as const,
    environments: (orgId?: string) => ["workflows", orgId, "environments"] as const,
    deployments:  (orgId?: string, id?: string) => ["workflows", orgId, id, "deployments"] as const,
    promotions:   (orgId?: string, id?: string) => ["workflows", orgId, id, "promotions"] as const,
    labels:       (orgId?: string) => ["workflows", orgId, "labels"] as const,
    categories:   (orgId?: string) => ["workflows", orgId, "categories"] as const,
  },

  webhooks: {
    all:   (orgId?: string) => ["webhooks", orgId] as const,
    list:  (orgId?: string, params?: { search?: string; status?: string; cursor?: string }) =>
             ["webhooks", orgId, "list", params] as const,
    stats: (orgId?: string, id?: string) => ["webhooks", orgId, id, "stats"] as const,
  },

  schedulers: {
    all:   (orgId?: string) => ["schedulers", orgId] as const,
    list:  (orgId?: string, params?: { search?: string; status?: string; cursor?: string }) =>
             ["schedulers", orgId, "list", params] as const,
    stats: (orgId?: string, id?: string) => ["schedulers", orgId, id, "stats"] as const,
  },

  dlq: {
    all:  (orgId?: string) => ["dlq", orgId] as const,
    list: (orgId?: string, triggerId?: string) => ["dlq", orgId, triggerId, "list"] as const,
  },

  appWebhooks: {
    all:  (orgId?: string) => ["app-webhooks", orgId] as const,
    list: (orgId?: string) => ["app-webhooks", orgId, "list"] as const,
  },

  authProviders: {
    all:  (orgId?: string) => ["auth-providers", orgId] as const,
    list: (orgId?: string, connectorId?: string) => ["auth-providers", orgId, "list", connectorId] as const,
  },

  connections: {
    all:  (orgId?: string) => ["connections", orgId] as const,
    list: (orgId?: string) => ["connections", orgId, "list"] as const,
  },

  secrets: {
    all:  (orgId?: string) => ["secrets", orgId] as const,
    list: (orgId?: string, endOrgId?: string) => ["secrets", orgId, "list", endOrgId] as const,
  },

  eventSubscriptions: {
    all:        (orgId?: string) => ["event-subscriptions", orgId] as const,
    list:       (orgId?: string) => ["event-subscriptions", orgId, "list"] as const,
    deliveries: (orgId?: string, id?: string) => ["event-subscriptions", orgId, "deliveries", id] as const,
  },

  saasConnectors: {
    all:  (orgId?: string) => ["saas-connectors", orgId] as const,
    list: (orgId?: string) => ["saas-connectors", orgId, "list"] as const,
    connectionsAll: (orgId?: string, connectorId?: string) => ["saas-connectors", orgId, "connections", connectorId] as const,
    connections: (orgId?: string, connectorId?: string, limit?: number, offset?: number) => ["saas-connectors", orgId, "connections", connectorId, limit ?? null, offset ?? null] as const,
  },

  envConfigs: {
    all:  (orgId?: string) => ["env-configs", orgId] as const,
    list: (orgId?: string) => ["env-configs", orgId, "list"] as const,
  },

  orchestrator: {
    all:      (orgId?: string) => ["orchestrator", orgId] as const,
    sessions: (orgId?: string) => ["orchestrator", orgId, "sessions"] as const,
    messages: (orgId?: string, threadId?: string) => ["orchestrator", orgId, "messages", threadId] as const,
    file:     (orgId?: string, threadId?: string, path?: string) => ["orchestrator", orgId, "file", threadId, path] as const,
    attachment: (orgId?: string, threadId?: string, attachmentId?: string) =>
      ["orchestrator", orgId, "attachment", threadId, attachmentId] as const,
    mcpApp: (orgId?: string, uri?: string) => ["orchestrator", orgId, "mcp-app", uri] as const,
  },

  signals: {
    all:      (orgId?: string) => ["signals", orgId] as const,
    health:   (orgId?: string) => ["signals", orgId, "health"] as const,
    list:     (orgId?: string, qs?: string) => ["signals", orgId, "list", qs] as const,
    detail:   (orgId?: string, id?: string) => ["signals", orgId, id] as const,
    crawlers: (orgId?: string) => ["signals", orgId, "crawlers"] as const,
  },

  aiModels: {
    all:  (orgId?: string) => ["ai-models", orgId] as const,
    list: (orgId?: string) => ["ai-models", orgId, "list"] as const,
  },

  unifiedConfig: {
    all: (orgId?: string) => ["unified-config", orgId] as const,
    options: (
      orgId?: string,
      widgetId?: string,
      entityId?: string,
      connectorSlug?: string,
      fieldKey?: string,
      params?: Record<string, unknown>,
      endOrgId?: string,
      pageSize?: number,
      query?: string,
    ) => ["unified-config", orgId, "options", widgetId, entityId, connectorSlug, fieldKey, params, endOrgId, pageSize, query] as const,
    /** One targeted lookup that resolves a single stored value's label — the
     *  page it lives on may never be loaded. Keyed apart from the paged list so
     *  it does not invalidate it. */
    optionLabel: (
      orgId?: string,
      widgetId?: string,
      entityId?: string,
      connectorSlug?: string,
      fieldKey?: string,
      params?: Record<string, unknown>,
      endOrgId?: string,
      value?: string,
    ) => ["unified-config", orgId, "option-label", widgetId, entityId, connectorSlug, fieldKey, params, endOrgId, value] as const,
  },

  unifiedCatalog: {
    all:             (orgId?: string) => ["unified-catalog", orgId] as const,
    categories:      (orgId?: string) => ["unified-catalog", orgId, "categories"] as const,
    entities:        (orgId?: string, category?: string) => ["unified-catalog", orgId, "entities", category] as const,
    entity:          (orgId?: string, id?: string) => ["unified-catalog", orgId, "entity", id] as const,
    runtimeCatalog:  (orgId?: string) => ["unified-catalog", orgId, "runtime-catalog"] as const,
    entityProviders: (category?: string, entity?: string) => ["unified-catalog", "providers", category, entity] as const,
    custom:          (orgId?: string) => ["unified-catalog", orgId, "custom"] as const,
  },

  entitlements: {
    all:      (orgId?: string) => ["entitlements", orgId] as const,
    me:       (orgId?: string) => ["entitlements", orgId, "me"] as const,
    platform: (orgId?: string) => ["entitlements", orgId, "platform"] as const,
    org:      (orgId?: string, targetOrgId?: string) => ["entitlements", orgId, "org", targetOrgId] as const,
    matrix:   (orgId?: string) => ["entitlements", orgId, "matrix"] as const,
  },

  v1Workspace: {
    all:               (orgId?: string) => ["v1-workspace", orgId] as const,
    org:               (orgId?: string, targetOrgId?: string) => ["v1-workspace", orgId, "org", targetOrgId] as const,
    matrix:            (orgId?: string) => ["v1-workspace", orgId, "matrix"] as const,
    platformPostgres:  (orgId?: string) => ["v1-workspace", orgId, "platform-postgres"] as const,
  },

  appTriggers: {
    all:       (orgId?: string) => ["app-triggers", orgId] as const,
    list:      (orgId?: string, params?: { search?: string; status?: string; cursor?: string; connectorId?: string }) =>
                 ["app-triggers", orgId, "list", params] as const,
    stats:     (orgId?: string, id?: string) => ["app-triggers", orgId, id, "stats"] as const,
    catalog:   (connectorId?: string) => ["app-triggers", "catalog", connectorId] as const,
    webhookConfigEvents: (connectorId?: string) => ["app-triggers", "webhook-config-events", connectorId] as const,
    webhookConfigConnectors: (orgId?: string) => ["app-triggers", "webhook-config-connectors", orgId] as const,
  },

  aiCosts: {
    all:            ()                                    => ["ai-costs"] as const,
    filterOptions:  ()                                    => ["ai-costs", "filter-options"] as const,
    summary:        (params?: Record<string, unknown>)    => ["ai-costs", "summary", params] as const,
    grouped:        (params?: Record<string, unknown>)    => ["ai-costs", "grouped", params] as const,
    logs:           (params?: Record<string, unknown>)    => ["ai-costs", "logs", params] as const,
    drilldown:      (by?: string, value?: string, params?: Record<string, unknown>) =>
                      ["ai-costs", "drilldown", by, value, params] as const,
  },

  workflowMetrics: {
    all:            ()                                 => ["workflow-metrics"] as const,
    summary:        (params?: Record<string, unknown>) => ["workflow-metrics", "summary", params] as const,
    topFailing:     (params?: Record<string, unknown>) => ["workflow-metrics", "top-failing", params] as const,
    recentFailures: (params?: Record<string, unknown>) => ["workflow-metrics", "recent-failures", params] as const,
    slowest:        (params?: Record<string, unknown>) => ["workflow-metrics", "slowest", params] as const,
    engine:         (params?: Record<string, unknown>) => ["workflow-metrics", "engine", params] as const,
    accounts:       ()                                 => ["workflow-metrics", "accounts"] as const,
  },

  platformMetrics: {
    all:        ()                     => ["platform-metrics"] as const,
    apiTraffic: (days?: number)        => ["platform-metrics", "api-traffic", days] as const,
    database:   ()                     => ["platform-metrics", "database"] as const,
  },

  apiKeys: {
    all:  (orgId?: string) => ["api-keys", orgId] as const,
    list: (orgId?: string) => ["api-keys", orgId, "list"] as const,
  },


  onboarding: {
    report:      (threadId?: string) => ["onboarding", "report", threadId] as const,
    // Org-scoped: every member of an org reads the same suggestion set, so the
    // key is keyed on the org and switching accounts refetches.
    suggestions: (orgId?: string)    => ["onboarding", "suggestions", orgId] as const,
    status:      ()                  => ["onboarding", "status"] as const,
    setup:       (orgId?: string)    => ["onboarding", "setup", orgId] as const,
    buildPlan:   (orgId: string | undefined, prompt: string) => ["onboarding", "build-plan", orgId, prompt] as const,
    orgDomain:   (orgId?: string)    => ["onboarding", "org-domain", orgId] as const,
  },

  widgets: {
    all:      (orgId?: string) => ["widgets", orgId] as const,
    list:     (orgId?: string) => ["widgets", orgId, "list"] as const,
    detail:   (orgId?: string, id?: string) => ["widgets", orgId, id] as const,
    docs:     (orgId?: string, id?: string, endOrgId?: string) => ["widgets", orgId, id, "docs", endOrgId ?? null] as const,
    insights: (orgId?: string, range?: string) => ["widgets", orgId, "insights", range] as const,
    // The partner's cross-customer rollup. Under the same "widgets" root as the per-customer
    // panel it aggregates, so anything that invalidates insights invalidates this too.
    tenantInsights: (orgId?: string) => ["widgets", orgId, "insights", "tenants"] as const,
    lineage:  (orgId?: string, ids?: string) => ["widgets", orgId, "lineage", ids] as const,
    embedStatus: (orgId?: string, widgetId?: string) => ["widgets", orgId, "embed-status", widgetId ?? null] as const,
    connectorLimit: (orgId?: string) => ["widgets", orgId, "connector-limit"] as const,
  },

  // KAN-1293: AI usage badge in the topbar. Scope=mine|org so switching the
  // slide-over toggle re-fetches without invalidating other consumers.
  aiUsage: {
    all:    (orgId?: string)                       => ["ai-usage", orgId] as const,
    byScope:(orgId?: string, scope?: "mine"|"org") => ["ai-usage", orgId, scope] as const,
    // Every org's usage, for callers acting on somebody else's org — a platform
    // admin approving a plan change does not hold the target org's id in their
    // own key space. `all()` with no argument is NOT this: it yields
    // ["ai-usage", undefined], which prefix-matches nothing and invalidates
    // nothing, silently.
    any:    ()                                     => ["ai-usage"] as const,
  },

  configs: {
    all:      (orgId?: string) => ["configs", orgId] as const,
    detail:   (orgId?: string, id?: string) => ["configs", orgId, id] as const,
    byWidget: (orgId?: string, widgetId?: string) => ["configs", orgId, "widget", widgetId] as const,
    /** Configs scoped to one customer. A separate key from `all`: that one
     *  holds templates only, because the endpoint returns templates unless it
     *  is given an end_org_id. */
    byEndOrg: (orgId?: string, endOrgId?: string, widgetId?: string) =>
      ["configs", orgId, "end-org", endOrgId, widgetId ?? null] as const,
  },

  configDrafts: {
    all:    (orgId?: string) => ["config-drafts", orgId] as const,
    detail: (orgId?: string, id?: string) => ["config-drafts", orgId, id] as const,
  },

  testCaseDrafts: {
    all:    (orgId?: string) => ["test-case-drafts", orgId] as const,
    detail: (orgId?: string, id?: string) => ["test-case-drafts", orgId, id] as const,
  },

  installations: {
    all:      (orgId?: string) => ["installations", orgId] as const,
    list:     (orgId?: string) => ["installations", orgId, "list"] as const,
    detail:   (orgId?: string, id?: string) => ["installations", orgId, id] as const,
    byTarget: (orgId?: string, targetType?: string, targetId?: string) =>
              ["installations", orgId, "target", targetType, targetId] as const,
    triggers: (orgId?: string, id?: string) => ["installations", orgId, id, "triggers"] as const,
  },

  hubSettings: {
    all:  (orgId?: string) => ["hub-settings", orgId] as const,
    get:  (orgId?: string) => ["hub-settings", orgId, "get"] as const,
  },

  userPreferences: {
    me: () => ["user-preferences", "me"] as const,
  },

  billing: {
    subscription: (orgId?: string) => ["billing", orgId, "subscription"] as const,
    invoices: (orgId?: string) => ["billing", orgId, "invoices"] as const,
    paymentMethod: (orgId?: string) => ["billing", orgId, "payment-method"] as const,
  },

  planChangeRequests: {
    all:     (orgId?: string)  => ["plan-change-requests", orgId] as const,
    mine:    (orgId?: string)  => ["plan-change-requests", orgId, "mine"] as const,
    pending: ()                => ["plan-change-requests", "admin", "pending"] as const,
  },

  actionReviews: {
    all:     ()                     => ["action-reviews"] as const,
    list:    (status?: string)      => ["action-reviews", "admin", status ?? "pending_review"] as const,
    detail:  (id?: string)          => ["action-reviews", "detail", id] as const,
    versions: (id?: string)         => ["action-reviews", "versions", id] as const,
  },
  monitoring: {
    all:    (orgId?: string) => ["monitoring", orgId] as const,
    list:   (orgId?: string, params?: Record<string, unknown>) =>
              ["monitoring", orgId, "list", params] as const,
    detail: (orgId?: string, id?: string) => ["monitoring", orgId, id, "detail"] as const,
    outcomes: (orgId?: string, id?: string, days?: number) => ["monitoring", orgId, id, "outcomes", days] as const,
    orgs:   () => ["monitoring", "orgs"] as const,
  },

  githubIntegration: {
    status: (orgId?: string) => ["github-integration", orgId, "status"] as const,
    reviews: (orgId?: string) => ["github-integration", orgId, "reviews"] as const,
    repositories: (orgId?: string) => ["github-integration", orgId, "repositories"] as const,
  },

  // A workspace override is the same resource addressed at the workspace's own
  // org id, so `config` is keyed by target rather than split into two groups.
  workspaceDb: {
    all:    (orgId?: string) => ["workspace-db", orgId] as const,
    config: (orgId?: string, targetOrgId?: string) =>
              ["workspace-db", orgId, "config", targetOrgId] as const,
    overrides: (orgId?: string, targetOrgId?: string) =>
              ["workspace-db", orgId, "overrides", targetOrgId] as const,
  },

  customers: {
    all:  (orgId?: string) => ["customers", orgId] as const,
    list: (orgId?: string) => ["customers", orgId, "list"] as const,
  },

  // Keyed by the org the config BELONGS to, not by the caller's org: the same
  // page reads its own account's document and a customer's, and org ids are
  // globally unique, so one id identifies the entry on its own.
  orgConfig: {
    detail: (orgId?: string) => ["org-config", orgId ?? null] as const,
  },

  // Keyed by the customer it was minted for: that is what makes switching
  // customers in the Widget Builder swap to that customer's token instead of
  // leaving the previous one on screen. `null` is the org-level admin preview.
  embedToken: {
    all:         (orgId?: string) => ["embed-token", orgId] as const,
    forCustomer: (orgId?: string, endOrgId?: string | null, userId?: string | null) =>
                   ["embed-token", orgId, endOrgId ?? null, userId ?? null] as const,
  },

  // Keyed by customer for the same reason embedToken is: the panel shows the
  // links for whichever customer is selected.
  embedShareLinks: {
    all:         (orgId?: string) => ["embed-share-links", orgId] as const,
    forCustomer: (orgId?: string, endOrgId?: string | null) =>
                   ["embed-share-links", orgId, endOrgId ?? null] as const,
    // Claude connections share the prefix, so a revoke's invalidation of `all` refreshes both lists.
    mcpForCustomer: (orgId?: string, endOrgId?: string | null) =>
                   ["embed-share-links", orgId, endOrgId ?? null, "mcp"] as const,
  },

  // The before-state the agent's approval card diffs a proposed change against.
  // Keyed by resource + id rather than reusing that resource's own detail key:
  // this read is a point-in-time snapshot for one card and must not be shared
  // with, or invalidated by, the page that owns the resource — a card showing
  // "before" would otherwise refetch into "after" the moment the approved call
  // invalidated the detail query, and the diff would collapse to nothing.
  changePreview: {
    all:      (orgId?: string) => ["change-preview", orgId] as const,
    snapshot: (orgId?: string, resource?: string, id?: string) =>
                ["change-preview", orgId, resource ?? null, id ?? null] as const,
  },

  db: {
    all:        () => ["db"] as const,
    overview:   () => ["db", "overview"] as const,
    connections: () => ["db", "connections"] as const,
    slowQueries: () => ["db", "slow-queries"] as const,
    tableSizes:  () => ["db", "table-sizes"] as const,
    stats:       () => ["db", "stats"] as const,
  },
} as const;
